import { memo, useMemo, useState, type ReactNode } from 'react';
import moment from 'moment-timezone';
import { StarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/confirm/ConfirmProvider';
import { mceTypeBadgeClass, statusBadgeClass, StatusBadge } from '@/components/statusBadges';
import { normalizeCamelCase } from '../ui/utils';

type Dated = { lastChangeAt?: string | number | null; createdAt?: string | number | null };

export type ContentHeaderProps = {
  showHeroIcon?: boolean;
  HeaderTitle?: ReactNode;
  status?: string;
  type?: string;
  adjustment?: boolean;
  hasPolicyIVE?: boolean;
};

/** The page title with its status, IVE, MCE-type and Adjusted chips. */
export function ContentHeader({ showHeroIcon, HeaderTitle, status, type, adjustment, hasPolicyIVE }: ContentHeaderProps) {
  const confirm = useConfirm();
  const [isHero, setIsHero] = useState(false);
  return (
    <div className="flex flex-row flex-wrap items-center gap-3">
      {showHeroIcon ? (
        <button
          type="button"
          aria-label="Hero quote"
          className="cursor-pointer"
          onClick={async () => {
            const confirmed = await confirm({
              title: 'Confirm Hero Quote Change',
              description: (
                <>
                  Are you sure you wish to set this quote as the Hero Quote?
                  <br />
                  This will remove the Hero status from the current Hero Quote.
                </>
              ),
              confirmLabel: 'Confirm',
            });
            if (confirmed) setIsHero(true);
          }}
        >
          <StarIcon className={cn('size-6', isHero ? 'fill-current' : undefined)} />
        </button>
      ) : null}
      <h3 className="text-2xl font-medium">{HeaderTitle}</h3>
      {status ? <StatusBadge className={statusBadgeClass(status)}>{normalizeCamelCase(status)}</StatusBadge> : null}
      {hasPolicyIVE && (status === 'Active' || status === 'Inactive') ? <StatusBadge className={statusBadgeClass('IVE')}>IVE</StatusBadge> : null}
      {type ? <StatusBadge className={mceTypeBadgeClass(type)}>{normalizeCamelCase(type)}</StatusBadge> : null}
      {status === 'Executed' && adjustment ? <StatusBadge className="bg-status-pending-bg text-status-adjusted-fg">Adjusted</StatusBadge> : null}
    </div>
  );
}

export type PageTitleAndActionProps = ContentHeaderProps & {
  data?: Dated | null;
  expiration?: { text?: string };
  actionButtons?: ReactNode | (() => ReactNode);
  effectiveDate?: unknown;
  itemNumber?: ReactNode;
};

const historyText = (data: Dated | null | undefined) => {
  if (!data?.lastChangeAt || data?.createdAt) return undefined;
  const lastTimestamp = data.lastChangeAt ? moment(data.lastChangeAt) : data.createdAt ? moment(data.createdAt) : null;
  if (!lastTimestamp) return 'Update History';
  const now = moment();
  const dayDiff = now.diff(lastTimestamp, 'days');
  if (dayDiff < 2) {
    const hourDiff = now.diff(lastTimestamp, 'hours');
    if (hourDiff < 1) {
      const minuteDiff = now.diff(lastTimestamp, 'minutes');
      if (minuteDiff <= 1) {
        return `Updated < 1 minute ago`;
      }
      return `Updated ${minuteDiff} minutes ago`;
    }
    return `Updated ${hourDiff} hour${hourDiff > 1 ? 's' : ''} ago`;
  } else if (dayDiff >= 9) {
    const monthDiff = now.diff(lastTimestamp, 'months');
    if (monthDiff >= 6) {
      return `Last updated ${lastTimestamp.format('M-D-YYYY')}`;
    }
    return `Last updated ${lastTimestamp.format('MMM Do')}`;
  }
  return `Updated ${now.diff(lastTimestamp, 'days')} days ago`;
};

/* The MCE page passes its actions as a function that uses hooks, and a new one on each render. Calling
   it inside this stable component, as the legacy Box called a function child, gives those hooks one
   owner: they keep their state across renders and never follow the title row's early return. */
function RenderActions({ render }: { render: () => ReactNode }) {
  return <>{render()}</>;
}

/** A policy or MCE page's title row: the chips, the actions, and the dates and numbers under it. */
const PageTitleAndAction = memo(function PageTitleAndAction({
  data,
  expiration,
  actionButtons,
  effectiveDate,
  itemNumber,
  HeaderTitle,
  ...rest
}: PageTitleAndActionProps) {
  const historyDisplayText = useMemo(() => historyText(data), [data]);
  if (!data) return null;
  return (
    <div className="bg-page-surface">
      <div className="flex w-full flex-row">
        <div className="w-1/2">
          <ContentHeader HeaderTitle={HeaderTitle} {...rest} />
        </div>
        <div className="w-1/2">{typeof actionButtons === 'function' ? <RenderActions render={actionButtons} /> : actionButtons}</div>
      </div>
      <ul className="flex list-none flex-row flex-wrap gap-x-6 gap-y-1.5 text-sm/[22px] font-bold text-page-meta">
        <li className="text-page-meta-strong">Created {moment.utc(effectiveDate as moment.MomentInput).format('MMM D, YYYY')}</li>
        {expiration?.text ? <li className="ml-4 list-disc">{expiration.text}</li> : null}
        <li className="ml-4 list-disc">{itemNumber}</li>
        {historyDisplayText ? <li className="ml-4 list-disc">{historyDisplayText}</li> : null}
        <li className="ml-4 list-disc">{HeaderTitle}</li>
      </ul>
    </div>
  );
});

export default PageTitleAndAction;
